import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchCertificationData, fetchMultipleCertifications } from '../data/qnetApiData';

const QNetDataContext = createContext();

/**
 * Q-Net 데이터 전역 관리 컨텍스트
 * - 앱 시작시 주요 자격증 데이터 사전 로딩
 * - LocalStorage 기반 영구 캐시
 * - 백그라운드 데이터 업데이트
 */
export const QNetDataProvider = ({ children }) => {
  const [dataCache, setDataCache] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errors, setErrors] = useState({});
  const [initialized, setInitialized] = useState(false);

  // 주요 자격증 코드 (사전 로딩 대상)
  const PRIORITY_CERTIFICATIONS = [
    '1150', // 전기기사
    '1320', // 정보처리기사
    '1431', // 산업안전기사
    '1441', // 산업위생관리기사
    '1630', // 건축기사
    '1900', // 소방설비기사(기계분야)
    '1910', // 소방설비기사(전기분야)
    '2140', // 전기산업기사
    '2590', // 공조냉동기계산업기사
    '2670', // 컴퓨터활용능력1급
    '2671', // 컴퓨터활용능력2급
    '7780', // 전기기능사
    '7875', // 지게차운전기능사
    '7910', // 한식조리기능사
  ];

  // LocalStorage 키
  const CACHE_KEY = 'qnet_certification_cache';
  const CACHE_TIMESTAMP_KEY = 'qnet_cache_timestamp';
  const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24시간

  /**
   * LocalStorage에서 캐시 데이터 로드
   */
  const loadCacheFromStorage = () => {
    try {
      const cacheData = localStorage.getItem(CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cacheData && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age < CACHE_EXPIRY) {
          const parsedCache = JSON.parse(cacheData);
          setDataCache(parsedCache);
          console.log('📦 LocalStorage에서 캐시 데이터 로드:', Object.keys(parsedCache).length, '개 자격증');
          return parsedCache;
        } else {
          console.log('⏰ 캐시 만료, 새로운 데이터 필요');
        }
      }
    } catch (error) {
      console.warn('LocalStorage 캐시 로드 실패:', error);
    }
    return {};
  };

  /**
   * LocalStorage에 캐시 데이터 저장
   */
  const saveCacheToStorage = (cache) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log('💾 LocalStorage에 캐시 저장 완료');
    } catch (error) {
      console.warn('LocalStorage 캐시 저장 실패:', error);
    }
  };

  /**
   * 특정 자격증 데이터 가져오기
   */
  const getCertificationData = async (examCode) => {
    if (!examCode) return null;

    // 캐시에 있으면 즉시 반환
    if (dataCache[examCode]) {
      return dataCache[examCode];
    }

    // 이미 로딩 중이면 대기
    if (loadingStates[examCode]) {
      return new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!loadingStates[examCode]) {
            clearInterval(checkLoading);
            resolve(dataCache[examCode] || null);
          }
        }, 100);
      });
    }

    // 새로운 데이터 로딩
    try {
      setLoadingStates(prev => ({ ...prev, [examCode]: true }));
      setErrors(prev => ({ ...prev, [examCode]: null }));

      const data = await fetchCertificationData(examCode);

      if (data && data.success) {
        const newCache = { ...dataCache, [examCode]: data };
        setDataCache(newCache);
        saveCacheToStorage(newCache);
        return data;
      } else {
        throw new Error('데이터 로딩 실패');
      }

    } catch (error) {
      console.error(`자격증 ${examCode} 데이터 로딩 실패:`, error);
      setErrors(prev => ({ ...prev, [examCode]: error.message }));
      return null;

    } finally {
      setLoadingStates(prev => ({ ...prev, [examCode]: false }));
    }
  };

  /**
   * 주요 자격증 데이터 사전 로딩
   */
  const preloadPriorityCertifications = async () => {
    console.log('🚀 주요 자격증 데이터 사전 로딩 시작...');
    
    const missingCodes = PRIORITY_CERTIFICATIONS.filter(code => !dataCache[code]);
    
    if (missingCodes.length === 0) {
      console.log('✅ 모든 주요 자격증 데이터가 이미 캐시됨');
      setInitialized(true);
      return;
    }

    try {
      // 5개씩 배치로 처리 (서버 부하 방지)
      const batchSize = 5;
      for (let i = 0; i < missingCodes.length; i += batchSize) {
        const batch = missingCodes.slice(i, i + batchSize);
        console.log(`📦 배치 ${Math.floor(i/batchSize) + 1} 로딩:`, batch);

        const promises = batch.map(code => getCertificationData(code));
        await Promise.allSettled(promises);

        // 배치 간 딜레이 (서버 부하 방지)
        if (i + batchSize < missingCodes.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      console.log('✅ 주요 자격증 데이터 사전 로딩 완료');

    } catch (error) {
      console.error('사전 로딩 실패:', error);
    } finally {
      setInitialized(true);
    }
  };

  /**
   * 백그라운드 데이터 업데이트 (12시간마다)
   */
  const backgroundUpdate = async () => {
    const lastUpdate = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!lastUpdate) return;

    const age = Date.now() - parseInt(lastUpdate);
    const updateThreshold = 12 * 60 * 60 * 1000; // 12시간

    if (age > updateThreshold) {
      console.log('🔄 백그라운드 데이터 업데이트 시작...');
      
      // 조용히 백그라운드에서 업데이트
      const cachedCodes = Object.keys(dataCache);
      for (const code of cachedCodes) {
        try {
          const freshData = await fetchCertificationData(code);
          if (freshData && freshData.success) {
            setDataCache(prev => ({ ...prev, [code]: freshData }));
          }
          // 요청 간 딜레이
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (error) {
          console.warn(`백그라운드 업데이트 실패 (${code}):`, error);
        }
      }

      saveCacheToStorage(dataCache);
      console.log('✅ 백그라운드 데이터 업데이트 완료');
    }
  };

  /**
   * 초기화
   */
  useEffect(() => {
    const initialize = async () => {
      // LocalStorage 캐시 로드
      const cachedData = loadCacheFromStorage();
      
      // 사전 로딩
      await preloadPriorityCertifications();
      
      // 백그라운드 업데이트
      setTimeout(backgroundUpdate, 5000); // 5초 후 백그라운드 업데이트
    };

    initialize();
  }, []);

  /**
   * 캐시 초기화 (개발/디버깅용)
   */
  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    setDataCache({});
    console.log('🗑️ 캐시 초기화 완료');
  };

  const contextValue = {
    // 데이터
    dataCache,
    loadingStates,
    errors,
    initialized,
    
    // 함수
    getCertificationData,
    clearCache,
    
    // 통계
    cacheSize: Object.keys(dataCache).length,
    priorityCount: PRIORITY_CERTIFICATIONS.length,
  };

  return (
    <QNetDataContext.Provider value={contextValue}>
      {children}
    </QNetDataContext.Provider>
  );
};

/**
 * Q-Net 데이터 컨텍스트 사용 훅
 */
export const useQNetData = () => {
  const context = useContext(QNetDataContext);
  if (!context) {
    throw new Error('useQNetData must be used within a QNetDataProvider');
  }
  return context;
};

export default QNetDataContext;